import { Link } from 'react-router-dom';
import {
  ShieldCheck, LineChart, Radio, ClipboardList, Building2,
  ArrowRight, Users, Bell, Search, FileCheck, Sparkles, Clock,
  Star, FileText, Banknote, UserCheck, BarChart3,
} from 'lucide-react';
import ProductMockup from '../../components/marketing/ProductMockup';

/**
 * Feb 2026 Marketing Redesign — the new HomePage.
 *
 * Answers exactly three questions, in this order:
 *   1. What is ShiftCover365?  → Hero
 *   2. Why should I trust it?  → Trust Strip, How It Works, Features, Industries
 *   3. How do I get started?    → Final CTA
 *
 * No pricing detail, no long paragraphs, no analytics dashboards. Every
 * section has ONE purpose. Massive whitespace (py-24/py-32). Larger
 * headlines, reduced body copy.
 */

const TRUST_ITEMS = [
  { label: 'Verified workers', icon: ShieldCheck },
  { label: 'Reliability history', icon: LineChart },
  { label: 'Live availability', icon: Radio },
  { label: 'Shift records', icon: ClipboardList },
  { label: 'Multi-site support', icon: Building2 },
];

const STEPS = [
  {
    num: '01',
    title: 'Post a shift',
    body: 'Tell us the branch, the hours and the rate. It takes under a minute.',
  },
  {
    num: '02',
    title: 'Review workers',
    body: 'See applicants with verified profiles, reliability history and previous shifts.',
  },
  {
    num: '03',
    title: 'Confirm cover',
    body: 'Pick the right person, and get a live audit trail for the shift start to finish.',
  },
];

const FEATURES = [
  { icon: Clock, title: 'Post & Fill Quickly', body: 'Create a shift, get applicants, fill it in minutes.' },
  { icon: ShieldCheck, title: 'Worker Verification', body: 'Smart ID and HR checks before anyone starts work.' },
  { icon: Star, title: 'Ratings & Reviews', body: 'Rate workers after shifts. Build trust over time.' },
  { icon: FileText, title: 'Shift Audit Trail', body: 'Complete history of every covered shift.' },
  { icon: Banknote, title: 'Till Accountability', body: 'Track till amounts start to finish with discrepancy alerts.' },
  { icon: UserCheck, title: 'HR Verification', body: 'Manager confirms Smart ID with HR support.' },
  { icon: Bell, title: 'Real-time Notifications', body: 'Instant alerts for applications, discrepancies, and issues.' },
  { icon: BarChart3, title: 'Reliability History', body: 'See cancellations, no-shows, and trust scores at a glance.' },
];

const INDUSTRIES = [
  {
    name: 'Post Offices',
    body: 'Trusted, till-accountable cover for branches during illness, holiday or busy periods.',
    image: 'https://images.unsplash.com/photo-1758795680241-ba9cd3fa2cc2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHw0fHxwb3N0JTIwb2ZmaWNlJTIwY291bnRlciUyMHdvcmtlcnxlbnwwfHx8fDE3ODU1MDgxMTZ8MA&ixlib=rb-4.1.0&q=85',
  },
  {
    name: 'Convenience Stores',
    body: 'Reliable front-of-till cover with a full shift audit trail for every session.',
    image: 'https://images.unsplash.com/photo-1759153820384-12c9ddf8bd8d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxjb252ZW5pZW5jZSUyMHN0b3JlJTIwbW9kZXJuJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzg1NTA4MTE2fDA&ixlib=rb-4.1.0&q=85',
  },
  {
    name: 'Retail',
    body: 'Cover the shop floor confidently — verified workers with retail experience only.',
    image: 'https://images.unsplash.com/photo-1777895610711-0a51df074e41?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHw0fHxyZXRhaWwlMjBzdG9yZSUyMGludGVyaW9yJTIwbW9kZXJufGVufDB8fHx8MTc4NTUwODExNnww&ixlib=rb-4.1.0&q=85',
  },
  {
    name: 'Hospitality',
    body: 'Same-day cover for baristas, waiting staff and hosts — with reliability scores you can trust.',
    image: 'https://images.unsplash.com/photo-1758526348403-b729c700d675?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxob3NwaXRhbGl0eSUyMHJlc3RhdXJhbnQlMjBzdGFmZiUyMHByZW1pdW18ZW58MHx8fHwxNzg1NTA4MTE2fDA&ixlib=rb-4.1.0&q=85',
  },
];

const H1 = 'text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-[#0B132B]';
const H2 = 'text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-[#0B132B]';
const EYEBROW = 'text-[11px] font-bold uppercase tracking-[0.22em] text-[#DA291C]';
const BODY = 'text-base sm:text-lg text-slate-500 leading-relaxed';
const CONTAINER = 'max-w-7xl mx-auto px-6 lg:px-8';

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* ========================= HERO ========================= */}
      <section className={`${CONTAINER} pt-32 pb-20 lg:pt-40 lg:pb-28`} data-testid="hero-section">
        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-14 lg:gap-16 items-center min-h-[70vh]">
          <div className="max-w-xl">
            <p className={EYEBROW} data-testid="hero-eyebrow">Trusted shift cover</p>
            <h1 className={`${H1} mt-5`} data-testid="hero-headline">
              Find shift cover
              <br />
              you can trust.
            </h1>
            <p className={`${BODY} mt-6 max-w-md`} data-testid="hero-subheadline">
              ShiftCover365 helps customer-facing businesses find suitable temporary
              workers quickly and confidently — with verified profiles, reliability
              history and a full shift audit trail.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Link
                to="/register?role=manager"
                className="inline-flex items-center gap-2 h-13 sm:h-14 px-7 rounded-full bg-[#DA291C] text-white text-[15px] font-semibold hover:bg-[#b91f13] transition-colors"
                style={{ paddingTop: 0, paddingBottom: 0 }}
                data-testid="hero-primary-cta"
              >
                Start Free Trial <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/how-it-works"
                className="inline-flex items-center gap-2 h-13 sm:h-14 px-7 rounded-full border border-slate-300 text-[#0B132B] text-[15px] font-semibold hover:bg-slate-50 transition-colors"
                style={{ paddingTop: 0, paddingBottom: 0 }}
                data-testid="hero-secondary-cta"
              >
                See how it works
              </Link>
            </div>
            <p className="mt-5 text-[12.5px] text-slate-500">
              14-day free trial · No card required.
            </p>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProductMockup />
          </div>
        </div>
      </section>

      {/* ========================= TRUST STRIP ========================= */}
      <section className="border-y border-slate-100 bg-slate-50/50" data-testid="trust-strip">
        <div className={`${CONTAINER} py-10`}>
          <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-y-6 gap-x-8">
            {TRUST_ITEMS.map((it) => (
              <li key={it.label} className="flex items-center gap-2.5 justify-center lg:justify-start" data-testid={`trust-item-${it.label.replace(/\s+/g, '-').toLowerCase()}`}>
                <it.icon className="h-4 w-4 text-slate-500 shrink-0" strokeWidth={2} />
                <span className="text-[13.5px] font-medium text-slate-700">{it.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ========================= HOW IT WORKS (navy) ========================= */}
      <section className="bg-[#0B132B] text-white py-24 lg:py-32" data-testid="how-it-works-section">
        <div className={CONTAINER}>
          <div className="max-w-2xl mb-16 lg:mb-20">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#DA291C]">How it works</p>
            <h2 className="mt-4 text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight">Three steps. One trusted platform.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">
            {STEPS.map((s) => (
              <div key={s.num} data-testid={`step-${s.num}`}>
                <p className="text-6xl md:text-7xl font-bold text-white/15 tracking-tighter tabular-nums">
                  {s.num}
                </p>
                <h3 className="mt-6 text-2xl font-semibold tracking-tight">
                  {s.title}
                </h3>
                <p className="mt-3 text-[15px] text-slate-300 leading-relaxed max-w-xs">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========================= THE SOLUTION (light grey) ========================= */}
      <section className="bg-[#F3F4F6] py-24 lg:py-32" data-testid="solution-section">
        <div className={CONTAINER}>
          <div className="text-center mb-14 lg:mb-16 max-w-3xl mx-auto">
            <p className={EYEBROW}>The Solution</p>
            <h2 className={`${H2} mt-4`}>A Better Way To Manage Cover</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
            {FEATURES.map((f) => (
              <div
                key={f.title}
                className="rounded-2xl bg-white border border-slate-200/70 p-6 hover:-translate-y-0.5 hover:shadow-[0_16px_40px_rgba(11,19,43,0.06)] transition-[transform,box-shadow] duration-300"
                data-testid={`feature-card-${f.title.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <div className="h-12 w-12 rounded-xl bg-[#DA291C]/10 flex items-center justify-center">
                  <f.icon className="h-6 w-6 text-[#DA291C]" strokeWidth={2} />
                </div>
                <h3 className="mt-5 text-base font-bold text-[#0B132B]">
                  {f.title}
                </h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                  {f.body}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-12 flex justify-center">
            <Link
              to="/features"
              className="inline-flex items-center gap-2 text-[15px] font-semibold text-[#0B132B] hover:gap-3 transition-[gap]"
              data-testid="features-see-all"
            >
              See every feature <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ========================= INDUSTRIES ========================= */}
      <section className={`${CONTAINER} py-24 lg:py-32`} data-testid="industries-section">
        <div className="max-w-2xl mb-16 lg:mb-20">
          <p className={EYEBROW}>Industries</p>
          <h2 className={`${H2} mt-4`}>Purpose-built for customer-facing teams.</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {INDUSTRIES.map((ind) => (
            <Link
              to="/industries"
              key={ind.name}
              className="group block rounded-3xl overflow-hidden border border-slate-200/70 bg-white hover:-translate-y-0.5 hover:shadow-[0_16px_40px_rgba(11,19,43,0.06)] transition-[transform,box-shadow] duration-300"
              data-testid={`industry-card-${ind.name.replace(/\s+/g, '-').toLowerCase()}`}
            >
              <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
                <img
                  src={ind.image}
                  alt={ind.name}
                  className="h-full w-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-[#0B132B] tracking-tight">
                  {ind.name}
                </h3>
                <p className="mt-2 text-[13.5px] text-slate-500 leading-relaxed">
                  {ind.body}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ========================= FINAL CTA ========================= */}
      <section className="bg-[#0B132B] text-white py-24 lg:py-32" data-testid="final-cta-section">
        <div className={`${CONTAINER} text-center max-w-3xl`}>
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#DA291C]">
            Get started
          </p>
          <h2 className="mt-5 text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight">
            Trusted cover.
            <br />
            <span className="text-slate-400">In minutes.</span>
          </h2>
          <p className="mt-6 text-lg text-slate-400 max-w-lg mx-auto leading-relaxed">
            Start your 14-day free trial today. No card required.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-3">
            <Link
              to="/register?role=manager"
              className="inline-flex items-center gap-2 h-14 px-8 rounded-full bg-[#DA291C] text-white text-[15px] font-semibold hover:bg-[#b91f13] transition-colors"
              style={{ paddingTop: 0, paddingBottom: 0 }}
              data-testid="final-cta-primary"
            >
              Start Free Trial <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 h-14 px-8 rounded-full border border-white/25 text-white text-[15px] font-semibold hover:bg-white/5 transition-colors"
              style={{ paddingTop: 0, paddingBottom: 0 }}
              data-testid="final-cta-secondary"
            >
              See pricing
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
